import { useState, useEffect } from 'react';
import './widget.scss';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ProductionQuantityLimitsIcon from '@mui/icons-material/ProductionQuantityLimits';

const Widget = ({ type, salesData }) => {
  const [data, setData] = useState({
    title: '',
    isMoney: false,
    link: '',
    icon: null,
    value: 0,
  });

  const apiUrl = process.env.REACT_APP_API_URL;

  const getMerchantIdFromToken = () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;

    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload).merchantId;
    } catch (e) {
      console.error('Error decoding token:', e);
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (type === 'revenue') {
          setData((prev) => ({
            ...prev,
            value: Number(salesData?.todaySales || 0).toFixed(2),
          }));
          return;
        }

        let url;
        const accessToken = localStorage.getItem('accessToken');
        const headers = {};

        if (type !== 'order') {
          headers.Authorization = `Bearer ${accessToken}`;
        }

        switch (type) {
          case 'merchant':
            url = `${apiUrl}/merchant/total`;
            break;

          case 'shop':
            url = `${apiUrl}/foods/total`;
            break;

          case 'order': {
            const merchantId = getMerchantIdFromToken();
            if (!merchantId) throw new Error('Merchant ID not found');
            url = `${apiUrl}/orders/total?merchantId=${merchantId}`;
            break;
          }

          default:
            throw new Error('Invalid widget type');
        }

        const response = await fetch(url, { headers });

        if (!response.ok) {
          throw new Error(`Failed to fetch data for ${type}`);
        }

        const responseData = await response.json();

        setData((prevData) => ({
          ...prevData,
          value: responseData.value ?? 0,
        }));
      } catch (error) {
        console.error(`Error fetching data for ${type}:`, error);
      }
    };

    fetchData();
  }, [type, apiUrl, salesData]);

  useEffect(() => {
    let widgetData = {};

    switch (type) {
      case 'merchant':
        widgetData = {
          title: 'TOTAL SHOPS',
          isMoney: false,
          link: 'See all shops',
          icon: (
            <StorefrontIcon
              className="icon"
              style={{
                color: 'crimson',
                backgroundColor: 'rgba(255, 0, 0, 0.2)',
              }}
            />
          ),
        };
        break;

      case 'shop':
        widgetData = {
          title: 'TOTAL FOODS',
          isMoney: false,
          link: 'View all foods',
          icon: (
            <ProductionQuantityLimitsIcon
              className="icon"
              style={{
                color: 'goldenrod',
                backgroundColor: 'rgba(218, 165, 32, 0.2)',
              }}
            />
          ),
        };
        break;

      case 'order':
        widgetData = {
          title: 'TOTAL ORDERS',
          isMoney: false,
          link: 'View all orders',
          icon: (
            <MonetizationOnIcon
              className="icon"
              style={{
                color: 'green',
                backgroundColor: 'rgba(0, 128, 0, 0.2)',
              }}
            />
          ),
        };
        break;

      case 'revenue':
        widgetData = {
          title: 'TOTAL SALES TODAY',
          isMoney: true,
          link: 'View all sales',
          icon: (
            <AccountBalanceWalletIcon
              className="icon"
              style={{
                color: 'purple',
                backgroundColor: 'rgba(128, 0, 128, 0.2)',
              }}
            />
          ),
        };
        break;

      default:
        widgetData = {};
    }

    setData((prevData) => ({ ...prevData, ...widgetData }));
  }, [type]);

  return (
    <div className="widget">
      <div className="left">
        <span className="title">{data.title}</span>
        <span className="counter">
          {data.isMoney ? 'RM ' : ''}
          {data.value}
        </span>
        <span className="link">{data.link}</span>
      </div>

      <div className="right">{data.icon}</div>
    </div>
  );
};

export default Widget;